import {
  Avatar,
  Card,
  EmptyState,
  ErrorState,
  Input,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  type TableSortDirection,
} from '@marrafa-gioale/gymx-ui-web'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { ChevronRight, Search, Users, X } from 'lucide-react'
import { useState } from 'react'
import { z } from 'zod'

import { ApiError, errorMessage } from '@/api/errors'
import { useUsers } from '@/api/queries'
import type { AdminUser, SortOrder, UserSort } from '@/api/types'
import { SORT_ORDERS, USER_ROLES, USER_SORTS } from '@/api/types'
import { CursorPager } from '@/components/data/cursor-pager'
import { PageHeader } from '@/components/layout/app-shell'
import { RoleBadge, StateBadge } from '@/features/users/user-badges'
import { avatarName, displayName, formatNumber, formatRelative, plural } from '@/lib/format'
import { useDebounced } from '@/lib/hooks'
import { sortableColumn } from '@/lib/sortable-column'
import { useCursorPagination } from '@/lib/use-cursor-pagination'
import { cn } from '@/lib/utils'

const searchSchema = z.object({
  q: z.string().optional(),
  role: z.enum(USER_ROLES).optional(),
  disabled: z.boolean().optional(),
  onboardingCompleted: z.boolean().optional(),
  sort: z.enum(USER_SORTS).optional(),
  order: z.enum(SORT_ORDERS).optional(),
})

type UserSearch = z.infer<typeof searchSchema>

export const Route = createFileRoute('/_app/utenti/')({
  validateSearch: searchSchema,
  component: UsersPage,
})

const PAGE_SIZE = 25

/** Default del server (`resolveSort(USER_SORTS, 'createdAt', …)`), replicato per l'indicatore. */
const DEFAULT_SORT: UserSort = 'createdAt'

/**
 * Verso naturale di ogni campo, uguale a `defaultDirection` lato server.
 *
 * Duplicato qui di proposito e non dedotto: serve a sapere **prima** della
 * richiesta quale freccia disegnare al primo click, e senza di esso il pannello
 * mostrerebbe la freccia in su su un elenco ordinato dal più recente.
 */
const SORT_DIRECTIONS: Record<UserSort, SortOrder> = {
  createdAt: 'desc',
  email: 'asc',
  lastLoginAt: 'desc',
}

const SORT_LABELS: Record<UserSort, string> = {
  createdAt: 'Data di registrazione',
  email: 'Email',
  lastLoginAt: 'Ultimo accesso',
}

function UsersPage() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })

  const [queryDraft, setQueryDraft] = useState(search.q ?? '')
  const debouncedQuery = useDebounced(queryDraft, 350)

  const sort = search.sort ?? DEFAULT_SORT
  const order = search.order ?? SORT_DIRECTIONS[sort]

  const params = {
    ...(debouncedQuery ? { query: debouncedQuery } : {}),
    ...(search.role ? { role: search.role } : {}),
    ...(search.disabled !== undefined ? { disabled: search.disabled } : {}),
    ...(search.onboardingCompleted !== undefined
      ? { onboardingCompleted: search.onboardingCompleted }
      : {}),
    sort,
    order,
    limit: PAGE_SIZE,
  }

  /**
   * `sort` e `order` stanno dentro `params`, quindi dentro la `resetKey`.
   *
   * Non è un dettaglio: il cursore del backend porta con sé la firma
   * dell'ordinamento con cui è stato emesso e risponde `400` se lo si presenta a
   * un ordinamento diverso — di proposito, perché riusarlo salterebbe righe a
   * caso invece di sbagliare in modo visibile. `useCursorPagination` azzera il
   * cursore **durante il render** in cui la chiave cambia, quindi la richiesta
   * con l'ordinamento nuovo parte già dalla prima pagina: non esiste un istante
   * in cui il cursore vecchio e il sort nuovo viaggiano insieme.
   */
  const pagination = useCursorPagination(JSON.stringify(params))
  const list = useUsers({ ...params, ...(pagination.cursor ? { cursor: pagination.cursor } : {}) })

  const setFilter = <K extends keyof UserSearch>(key: K, value: UserSearch[K]) => {
    void navigate({
      search: (previous) => {
        const next = { ...previous }
        if (value === undefined) delete next[key]
        else next[key] = value
        return next
      },
    })
  }

  /** L'ordinamento vive in query string come i filtri: un elenco ordinato è un link. */
  const setSort = (field: UserSort, direction: TableSortDirection) => {
    void navigate({ search: (previous) => ({ ...previous, sort: field, order: direction }) })
  }

  /** Le props di ordinamento, colonna per colonna. */
  const column = sortableColumn(sort, order, setSort)

  const hasFilters =
    Boolean(queryDraft) ||
    search.role !== undefined ||
    search.disabled !== undefined ||
    search.onboardingCompleted !== undefined

  const clearFilters = () => {
    setQueryDraft('')
    // L'ordinamento sopravvive all'azzeramento: non è un filtro, è il modo in cui
    // si sta guardando l'elenco, e ributtarlo al default sarebbe una seconda
    // azione che nessuno ha chiesto.
    void navigate({ search: { sort: search.sort, order: search.order } })
  }

  const rows = list.data?.data ?? []

  return (
    <>
      <PageHeader
        title="Utenti"
        description="Ruoli, stato dell'account e supporto. Le schede di un utente si consultano dal dettaglio."
      />

      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
        <div className="relative min-w-0 flex-1 sm:max-w-xs">
          <Search
            className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2"
            aria-hidden
          />
          <Input
            value={queryDraft}
            onChange={(event) => {
              setQueryDraft(event.target.value)
              setFilter('q', event.target.value || undefined)
            }}
            placeholder="Cerca per email o nome…"
            aria-label="Cerca utenti"
            className="pl-9"
            type="search"
          />
        </div>

        <div className="grid grid-cols-2 gap-2 sm:flex sm:w-auto">
          <Select
            aria-label="Filtra per ruolo"
            value={search.role ?? 'all'}
            onValueChange={(value) =>
              setFilter('role', value === 'all' ? undefined : (value as UserSearch['role']))
            }
            className="sm:w-36"
            items={[
              { value: 'all', label: 'Ogni ruolo' },
              { value: 'user', label: 'Utente' },
              { value: 'admin', label: 'Admin' },
            ]}
          />

          <Select
            aria-label="Filtra per stato account"
            value={search.disabled === undefined ? 'all' : search.disabled ? 'yes' : 'no'}
            onValueChange={(value) =>
              setFilter('disabled', value === 'all' ? undefined : value === 'yes')
            }
            className="sm:w-40"
            items={[
              { value: 'all', label: 'Ogni stato' },
              { value: 'no', label: 'Attivi' },
              { value: 'yes', label: 'Disabilitati' },
            ]}
          />

          <Select
            aria-label="Filtra per stato onboarding"
            value={
              search.onboardingCompleted === undefined
                ? 'all'
                : search.onboardingCompleted
                  ? 'yes'
                  : 'no'
            }
            onValueChange={(value) =>
              setFilter('onboardingCompleted', value === 'all' ? undefined : value === 'yes')
            }
            className="col-span-2 sm:w-48"
            items={[
              { value: 'all', label: 'Ogni onboarding' },
              { value: 'yes', label: 'Onboarding completato' },
              { value: 'no', label: 'Onboarding incompleto' },
            ]}
          />

          {/* Sotto `lg` non c'è una tabella su cui cliccare: l'elenco è di card e
              l'ordinamento passa da un select. Le due forme scrivono negli stessi
              parametri di query string, quindi restano allineate senza sforzo. */}
          <Select
            aria-label="Ordina l'elenco"
            value={`${sort}:${order}`}
            onValueChange={(value) => {
              const [field, direction] = value.split(':')
              setSort(field as UserSort, direction as TableSortDirection)
            }}
            className="col-span-2 lg:hidden"
            items={USER_SORTS.flatMap((field) =>
              SORT_ORDERS.map((direction) => ({
                value: `${field}:${direction}`,
                label: `${SORT_LABELS[field]} — ${direction === 'asc' ? 'crescente' : 'decrescente'}`,
              })),
            )}
          />
        </div>

        {hasFilters ? (
          <button
            type="button"
            onClick={clearFilters}
            className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 self-start text-sm underline-offset-4 hover:underline sm:ml-auto"
          >
            <X className="size-3.5" />
            Azzera filtri
          </button>
        ) : null}
      </div>

      {list.isError && !list.data ? (
        <ErrorState
          message={errorMessage(list.error)}
          requestId={list.error instanceof ApiError ? list.error.requestId : undefined}
          onRetry={() => void list.refetch()}
        />
      ) : list.isPending ? (
        <ListSkeleton />
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<Users />}
          title={hasFilters ? 'Nessun utente con questi filtri' : 'Nessun utente registrato'}
          description={
            hasFilters ? 'Prova ad allargare la ricerca.' : 'Gli account si creano dall’app.'
          }
        />
      ) : (
        <>
          <div className={cn('transition-opacity', list.isFetching && 'opacity-60')}>
            <ul className="flex flex-col gap-2 lg:hidden">
              {rows.map((user) => (
                <UserCard key={user.id} user={user} />
              ))}
            </ul>

            <div className="border-border bg-card hidden rounded-lg border lg:block">
              <Table>
                <TableHead>
                  <TableRow className="hover:bg-transparent">
                    {/* Solo le colonne che il server sa ordinare sono cliccabili:
                        «Stato» e «Schede» restano intestazioni mute perché
                        renderle cliccabili prometterebbe un ordinamento che
                        `AdminUserListQuery` non accetta — e la promessa
                        fallirebbe con un `400`. */}
                    <TableHeaderCell {...column('email', SORT_DIRECTIONS.email)}>
                      Utente
                    </TableHeaderCell>
                    <TableHeaderCell>Stato</TableHeaderCell>
                    <TableHeaderCell className="text-right">Schede</TableHeaderCell>
                    <TableHeaderCell {...column('lastLoginAt', SORT_DIRECTIONS.lastLoginAt)}>
                      Ultimo accesso
                    </TableHeaderCell>
                    <TableHeaderCell {...column('createdAt', SORT_DIRECTIONS.createdAt)}>
                      Registrato
                    </TableHeaderCell>
                    <TableHeaderCell className="w-10">
                      <span className="sr-only">Dettaglio</span>
                    </TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((user) => (
                    <UserRow key={user.id} user={user} />
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          <CursorPager
            meta={list.data?.meta}
            page={pagination.page}
            pageCount={rows.length}
            canGoBack={pagination.canGoBack}
            onNext={() => pagination.next(list.data?.meta.nextCursor)}
            onPrevious={pagination.previous}
            isFetching={list.isFetching}
            itemLabel="utenti"
          />
        </>
      )}
    </>
  )
}

function UserRow({ user }: { user: AdminUser }) {
  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-3">
          <Avatar name={avatarName(displayName(user))} size="md" />
          <div className="min-w-0">
            <Link
              to="/utenti/$userId"
              params={{ userId: user.id }}
              className="block max-w-xs truncate text-sm font-medium hover:underline"
            >
              {displayName(user)}
            </Link>
            <p className="text-muted-foreground max-w-xs truncate text-xs">{user.email}</p>
          </div>
        </div>
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap items-center gap-1.5">
          <StateBadge user={user} />
          <RoleBadge user={user} />
        </div>
      </TableCell>
      <TableCell className="text-right" data-numeric>
        {formatNumber(user.cardCount)}
      </TableCell>
      <TableCell className="text-muted-foreground text-sm">
        {formatRelative(user.lastLoginAt)}
      </TableCell>
      <TableCell className="text-muted-foreground text-sm">
        {formatRelative(user.createdAt)}
      </TableCell>
      <TableCell>
        <Link
          to="/utenti/$userId"
          params={{ userId: user.id }}
          className="text-muted-foreground hover:text-foreground flex justify-end transition-colors"
          aria-label={`Apri ${displayName(user)}`}
        >
          <ChevronRight className="size-4" />
        </Link>
      </TableCell>
    </TableRow>
  )
}

function UserCard({ user }: { user: AdminUser }) {
  return (
    <li>
      <Card radius="md" padding="none">
        <Link
          to="/utenti/$userId"
          params={{ userId: user.id }}
          className="flex items-start gap-3 p-3"
        >
          <Avatar name={avatarName(displayName(user))} size="md" />

          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{displayName(user)}</p>
            <p className="text-muted-foreground truncate text-xs">{user.email}</p>

            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <StateBadge user={user} />
              <RoleBadge user={user} />
            </div>

            <p className="text-muted-foreground mt-2 text-xs">
              {plural(user.cardCount, 'scheda', 'schede')} · accesso{' '}
              {formatRelative(user.lastLoginAt)}
            </p>
          </div>

          <ChevronRight className="text-muted-foreground mt-1 size-4 shrink-0" aria-hidden />
        </Link>
      </Card>
    </li>
  )
}

function ListSkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 8 }, (_, index) => (
        <div
          key={index}
          className="border-border bg-card flex items-center gap-3 rounded-md border p-3"
        >
          <Skeleton className="rounded-pill size-9 shrink-0" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-52" />
          </div>
          <Skeleton className="h-6 w-16 shrink-0" />
        </div>
      ))}
    </div>
  )
}
